import { useCallback, useMemo, useEffect, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  BellAlertIcon,
  ChatBubbleLeftRightIcon,
  MapIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import {
  ClipboardDocumentListIcon,
  ListBulletIcon,
  SparklesIcon,
} from "@heroicons/react/20/solid";
import { BsClipboardX } from "react-icons/bs";
import usePersistedState from "common/hooks/usePersistedState";
import { Thread } from "types/Thread";
import useItem from "common/hooks/useItem";
import useRouter from "common/hooks/use-router";
import useList from "common/hooks/useList";
import useAuth from "common/hooks/useAuth";
import Header from "common/components/Header/Header";
import FilteredList from "common/components/Table/FilteredList";
import RadioBlock from "common/components/RadioBlock/RadioBlock";
import TabBlockItem from "common/components/TabBlockItem/TabBlockItem";
import getDefaultFilters from "common/utils/getDefaultFilters";
import ThreadTypeSelect from "components/select/ThreadTypeSelect/ThreadTypeSelect";
import WorkSiteIcon from "components/Icon/icons/WorkSiteIcon";
import SiteSelect from "components/select/SiteSelect/SiteSelect";
import useOrganization from "common/hooks/useOrganization";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
import getWording from "common/utils/wording";
import AlertsContext from "common/providers/alerts";
import PostSelect from "components/select/PostSelect/PostSelect";
import EmptyBlockWithCTA from "common/components/EmptyBlockWithCTA/EmptyBlockWithCTA";
import UserSelect from "components/select/UserSelect/UserSelect";
import TagSelect from "components/select/TagSelect/TagSelect";
import { TagIcon } from "@heroicons/react/24/solid";
import Button from "common/components/Button/Button";
import fetchBinary from "common/utils/fetchBinary";
import dayjs from "dayjs";
import { endOfDay, startOfDay } from "date-fns";
import qs from "qs";
import ButtonGroup from "common/components/ButtonGroup/ButtonGroup";
import MapScreen from "pages/Threads/MapScreen";
import DatePicker from "common/components/DatePicker/DatePicker";
import ThreadItem from "./ThreadItem";
import { Constants } from "../../constants/Constants";

function EmptyThreadDataWithCTA() {
  const { t } = useTranslation();
  // Extraire les appels à getWording en dehors des hooks
  const defaultWording = getWording();

  return (
    <EmptyBlockWithCTA
      title={t("emptyDatasCta.noThreadsYet")}
      content={t("emptyDatasCta.forThreads", { wording: defaultWording })}
      actionTitle={t("emptyDatasCta.addSiteSoft", {
        wording: defaultWording,
      })}
      actionUrl="/sites/add"
    />
  );
}

function Threads() {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const { organization } = useOrganization();
  const { setAlert } = useContext(AlertsContext);

  const [threadView, setThreadView] = usePersistedState(
    "list",
    "isThreadMapSelected"
  );

  const defaultFilters = getDefaultFilters({
    currentUser,
    sitesIdsPath: "site][id",
  });

  const {
    fetchItems,
    items,
    isFetching,
    isItemsValid,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error: errorList1,
  } = useList<Thread>("threads/find-including-site-children", {
    defaultFilters: {
      ...defaultFilters,
      unkonwnSite: true,
    },
    defaultSorts: [
      "updatedAt:DESC",
      // "messages.privateThreadIntern?.messages?.updatedAt:DESC", PB: les messages internes existent pas forcément
    ],
    cachePrefix: `Threads_threads_${organization?.id || "all"}`,
    defaultItemsPerPage: threadView === "map" ? 500 : 25,
  });

  const { items: siteItems } = useList("sites", {
    defaultFilters: organization?.id
      ? {
          organization: organization.id,
        }
      : {},
    populate: ["site", "post"],
    cachePrefix: `Sites_inThreads_${organization?.id || "all"}`,
  });

  useEffect(() => {
    if (errorList1) {
      setAlert(errorList1, "danger");
    }
  }, [errorList1, setAlert]);

  const { navigate, location } = useRouter();

  const filterOnPost = location?.state?.filterOnPost;
  const filterOnSite = location?.state?.filterOnSite;

  const {
    items: dataThreads,
    setFilter: setFilterDataThreads,
    error: errorList2,
  } = useList<Thread>("threads/find-including-site-children", {
    defaultFilters: {
      ...defaultFilters,
      myThreads: !currentUser?.isAdmin,
      /* site: {
        active: true,
        disabled: false,
      }, */
    },
    populate: ["site", "post"],
    defaultItemsPerPage: 5000,
    cachePrefix: `Threads_threads2_${organization?.id || "all"}`,
  });

  useEffect(() => {
    if (errorList2) {
      setAlert(errorList2, "danger");
    }
  }, [errorList2, setAlert]);

  useEffect(() => {
    setFilter("site][organization", organization?.id || false);
    setFilterDataThreads("site][organization", organization?.id || false);

    if (filterOnSite) {
      setFilter("site][id", filterOnSite);
      setFilterDataThreads("site][id", filterOnSite);
    }

    if (filterOnPost) {
      setFilter("post][id", filterOnPost);
      setFilterDataThreads("post][id", filterOnPost);
    }
  }, [
    organization,
    setFilter,
    setFilterDataThreads,
    currentUser,
    filterOnPost,
    filterOnSite,
  ]);

  const statusList = useMemo(() => {
    return [
      {
        id: "",
        name: t("status.all"),
        count: dataThreads?.length || 0,
        icon: ListBulletIcon,
        color: "secondary",
      },
      {
        id: "waiting_answer",
        name: t("status.waiting_answer"),
        count:
          dataThreads?.filter((item: any) => item.status === "waiting_answer")
            ?.length || 0,
        icon: ClipboardDocumentListIcon,
        color: "yellow",
      },
      {
        id: "answered",
        name: t("status.answered"),
        count:
          dataThreads?.filter((item: any) => item.status === "answered")
            ?.length || 0,
        icon: SparklesIcon,
        color: "green",
      },
      {
        id: "closed",
        name: t("status.closed"),
        count:
          dataThreads?.filter((item: any) => item.status === "closed")
            ?.length || 0,
        icon: BsClipboardX,
        color: "slate",
      },
    ];
  }, [dataThreads, t]);

  const { removeItem } = useItem<Thread>("threads", "");

  const handleNavigate = (id: any) => {
    navigate(`/threads/${id}`);
  };

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [removeListItem, removeItem, fetchItems, setAlert]
  );

  const siteWording = getWording(false, true);

  const columns = useMemo(
    () => [
      {
        Header: t("forms.site", {
          wording: siteWording,
        }),
        filterName: "site][id",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <SiteSelect
            isFilter
            allowReadOnlyOptions
            Icon={WorkSiteIcon}
            alsoOnChange={() => setFilter("post][id", "")}
            {...props}
          />
        ),
      },
      {
        Header: t("titlePages.posts"),
        filterName: "post][id",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <PostSelect
            isFilter
            filterOnSiteId={filters["site][id"]}
            Icon={WorkSiteIcon}
            {...props}
          />
        ),
      },
      {
        Header: t("forms.type"),
        filterName: "type][id",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <ThreadTypeSelect isFilter Icon={BellAlertIcon} {...props} />
        ),
      },
      {
        Header: t("forms.tag"),
        filterName: "tags][id",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <TagSelect isFilter icon={TagIcon} {...props} />
        ),
      },
      {
        Header: t("forms.assignedUsers"),
        filterName: "assignedUsers][id",
        filterLabel: t("forms.assignedUsers"),
        filterComponent: (props: any) => (
          <UserSelect isFilter Icon={UserIcon} {...props} />
        ),
        columnClasses: "md:flex-1",
      },
      {
        Header: t("filters.publicContent"),
        filterName: "messages][content$contains",
        filterLabel: t("filters.search"),
        columnClasses: "md:flex-1",
      },
      {
        Header: t("filters.startDate"),
        filterName: "createdAt$gte",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <DatePicker
            placeholder={t("filters.startDate")}
            value={props.value ?? null}
            onChange={(date: Date | null) => {
              props.onChange(date ? startOfDay(date).toISOString() : "");
            }}
          />
        ),
      },
      {
        Header: t("filters.endDate"),
        filterName: "createdAt$lte",
        columnClasses: "md:flex-1",
        filterComponent: (props: any) => (
          <DatePicker
            placeholder={t("filters.endDate")}
            value={props.value ?? null}
            onChange={(date: Date | null) => {
              props.onChange(date ? endOfDay(date).toISOString() : "");
            }}
          />
        ),
      },
      {
        Header: t("forms.myThreads"),
        filterName: "myThreads",
        filterComponent: (props: any) => (
          <div className="pt-2">
            <ToggleSwitch
              rightLabel={t("forms.myThreads")}
              {...props}
              /* onChange={(value: boolean) => {
                props.onChange(value ? currentUser?.id : false);
              }}
              value={Boolean(props.value)} */
            />
          </div>
        ),
        columnClasses: "md:flex-1",
      },
    ],
    [t, siteWording, setFilter, filters]
  );

  const setOverallFilter = useCallback(
    (key: string, value: string | number | boolean) => {
      setFilter(key, value);
      setFilterDataThreads(key, value);
    },
    [setFilter, setFilterDataThreads]
  );

  const isBlank = !isItemsValid && !isFetching;

  const exportThreads = useCallback(async () => {
    // with the same filters as the view call the export threads endpoint
    const query = {
      filters: {},
    } as {
      filters: {
        [key: string]:
          | {
              [key: string]: string | number | boolean;
            }
          | object;
      };
    };
    // Logique issue de useList
    Object.keys(filters).forEach((filter) => {
      if (
        typeof filters[filter] === "string" ||
        filters[filter] instanceof Date
      ) {
        const [fieldName, operator = "eq"] = filter.split("$");
        if (operator === "date") {
          query.filters[fieldName] = {
            [`$gte`]: startOfDay(filters[filter] as Date),
            [`$lte`]: endOfDay(filters[filter] as Date),
          };
        } else if (!operator.includes("or]")) {
          query.filters[fieldName] = { [`$${operator}`]: filters[filter] };
        } else {
          query.filters[filter] = filters[filter] as object;
        }
      } else {
        query.filters[filter] = filters[filter] as object;
      }
    });
    const queryParams = qs.stringify(query, {
      encodeValuesOnly: true,
    });

    try {
      // Utiliser fetchBinary au lieu de fetchJSON pour récupérer le fichier XLSX
      const blob = await fetchBinary({
        url: `threads/export?${queryParams}`,
        method: "GET",
      });

      if (blob) {
        // Créer une URL pour le blob
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        // Utiliser l'extension .xlsx pour correspondre au format envoyé par le backend
        link.download = `Echanges_${dayjs().format(
          "YYYY-MM-DD_HH-mm-ss"
        )}.xlsx`;
        link.click();
        URL.revokeObjectURL(url);

        // Afficher un message de succès
        setAlert({
          type: "success",
          message: t("common.exportSuccess") || "Export réussi",
        });
      }
    } catch (error) {
      console.error("Erreur lors de l'export:", error);
      setAlert({
        type: "error",
        message: t("common.exportError") || "Erreur lors de l'export",
      });
    }
  }, [filters, setAlert, t]);

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.threads")}
        // subtitle={t("titleLegends.threads")}
        Icon={ChatBubbleLeftRightIcon}
        isBeforeFilters
        filters={filters}
        setFilter={setOverallFilter}
        filterName="status$contains"
        FilterComponent={(props: any) => (
          <div className="flex flex-col lg:flex-row justify-between w-full">
            <RadioBlock
              Component={TabBlockItem}
              items={statusList}
              horizontal
              noGap
              {...props}
            />
            <div className="flex flex-row items-center mb-2 mt-2 lg:-mt-2 flex-shrink-0 gap-2">
              <ButtonGroup
                value={threadView}
                onChange={setThreadView}
                options={[
                  {
                    label: (
                      <div className="flex flex-row items-center gap-2">
                        <ListBulletIcon className="w-4 h-4" />
                        <span>Liste</span>
                      </div>
                    ),
                    value: "list",
                  },
                  ...(Constants.IS_THREADS_MAP_ENABLED
                    ? [
                        {
                          label: (
                            <div className="flex flex-row items-center gap-2">
                              <MapIcon className="w-4 h-4" />
                              <span>Carte</span>
                            </div>
                          ),
                          value: "map",
                        },
                      ]
                    : []),
                ]}
                compact
              />
              {(currentUser?.isAdmin || currentUser?.isSuperadmin) && (
                <Button
                  className="flex-shrink-0"
                  type="primary-line"
                  title={t("actions.export")}
                  onClick={() => exportThreads()}
                />
              )}
            </div>
          </div>
        )}
      />

      {threadView === "map" && (
        <MapScreen
          threads={items ?? []}
          columns={columns}
          filters={filters}
          setFilter={setOverallFilter}
          setPageIndex={setPageIndex}
        />
      )}
      {threadView === "list" && (
        <FilteredList
          data={items ?? []}
          columns={columns} // used as filter items
          backLink={
            filterOnPost ? "/posts" : filterOnSite ? "/sites" : undefined
          }
          isFetching={isFetching}
          pageCount={pageCount}
          filters={filters}
          setFilter={setOverallFilter}
          setPageIndex={setPageIndex}
          pageIndex={pageIndex}
          RenderItem={ThreadItem}
          RenderEmptyBlock={
            !dataThreads?.length && !siteItems?.length && !isBlank
              ? EmptyThreadDataWithCTA
              : undefined
          }
          /* subtilité : on peut avoir des échanges alors qu'on a pas de sites (cf futur dev), donc on affiche le panel
      "pas encore d'échange, btw pourquoi pas créer un 1er site ?" seulement si on a aucuns des deux */
          onDelete={handleDelete}
          onNavigate={handleNavigate}
          itemsCount={itemsCount}
          itemsPerPage={itemsPerPage}
        />
      )}
    </div>
  );
}

export default Threads;
